"use client";

import { useMemo } from "react";

import { useAuth } from "@/features/auth";
import {
  detectHighlights,
  type HighlightResult,
} from "@/features/candidate/lib/highlights";
import type { ProfileData } from "@/features/candidate/lib/types";

import { filterHighlightsByComplianceSettings } from "../lib/filter-highlights";

export function resolveProfileHighlights(
  profile: ProfileData,
  hiddenProfileHighlightIds: readonly string[] | null | undefined,
  showProfileHighlights = true,
): HighlightResult[] {
  if (!showProfileHighlights) return [];

  const raw = Array.isArray(profile.highlights)
    ? (profile.highlights as HighlightResult[])
    : detectHighlights(profile);

  return filterHighlightsByComplianceSettings(raw, hiddenProfileHighlightIds);
}

/** Detect profile highlights and apply org privacy hidden-highlight policy. */
export function useProfileHighlights(profile: ProfileData): HighlightResult[] {
  const { complianceSettings } = useAuth();

  return useMemo(
    () =>
      resolveProfileHighlights(
        profile,
        complianceSettings?.hidden_profile_highlight_ids,
        complianceSettings?.show_profile_highlights ?? true,
      ),
    [
      profile,
      complianceSettings?.hidden_profile_highlight_ids,
      complianceSettings?.show_profile_highlights,
    ],
  );
}
